import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject, OnInit, signal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatTooltipModule } from '@angular/material/tooltip';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { ConnectorConfiguration, ConnectorSetupPhase } from '@blaze-shared/connectors-domain';
import {
  CopyConnectorConfigEnvButtonComponent,
  DialogModule,
  EnvironmentIndicatorComponent,
} from '@blaze-shared/shared-feature';
import {
  ButtonBarComponent,
  ButtonComponent,
  ElementLoadingIndicatorComponent,
  PanelComponent,
  PanelRef,
} from '@blaze-shared/shared-ui';
import { IconsModule } from '@blaze-shared/theming-data-access';

import { BaseConnectorDialogComponent } from '../../__base-connector-dialog/base-connector-dialog.component';
import { prepareConnectorConfiguration } from '../../utils';

import { ConnectorForm, DropboxConfiguration } from './dropbox-connector-dialog.models';

@UntilDestroy()
@Component({
  selector: 'blaze-dropbox-connector-dialog',
  templateUrl: './dropbox-connector-dialog.component.html',
  styleUrls: ['./dropbox-connector-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AsyncPipe,
    ButtonBarComponent,
    ButtonComponent,
    CopyConnectorConfigEnvButtonComponent,
    DialogModule,
    ElementLoadingIndicatorComponent,
    EnvironmentIndicatorComponent,
    IconsModule,
    MatTooltipModule,
    NgTemplateOutlet,
    PanelComponent,
    ReactiveFormsModule,
  ],
  host: { class: 'data-connector-dialog' },
})
export class DropboxConnectorDialogComponent
  extends BaseConnectorDialogComponent<DropboxConfiguration>
  implements OnInit
{
  private readonly panelRef = inject(PanelRef);

  serviceName = 'dropbox';
  connectorForm: FormGroup<ConnectorForm>;
  loading = signal(false);

  constructor() {
    super();

    this.connectorForm = new FormGroup<ConnectorForm>({
      connectorName: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    });

    this.withOAuth();
  }

  ngOnInit(): void {
    if (this.connector) {
      this.fillForm(this.getConfiguration(this.connector));
    } else {
      this.goToAuth();
    }
  }

  getConfiguration(connector?: ConnectorConfiguration): DropboxConfiguration {
    const connectorId = connector?.id ?? '';
    const connectorName = connector?.name ?? '';
    const configuration = connector?.configuration ?? {};
    const authState = String(configuration?.authState ?? '');

    return {
      authState,
      connectorId,
      connectorName,
    };
  }

  fillForm(configuration: DropboxConfiguration): void {
    const { authState, connectorId, connectorName } = configuration;

    this.authState = authState;
    this.connectorId = connectorId;
    this.setupPhase$$.next(ConnectorSetupPhase.ConnectorCreation);

    this.connectorForm.patchValue(
      {
        connectorName: connectorName,
      },
      { emitEvent: false },
    );
  }

  saveConnector(): void {
    if (this.connectorForm.invalid) {
      return;
    }

    const { connectorName } = this.connectorForm.value;

    if (!connectorName) {
      return;
    }

    const connectorConfig = prepareConnectorConfiguration(
      connectorName,
      this.template?.id,
      this.accountId,
      this.connector,
      {
        authState: this.authState,
      },
    );

    this.loading.set(true);
    this.saveOrUpdateConnector(connectorConfig, this.connector?.id)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (connector) => {
          if (connector) {
            this.panelRef.close();
          }
        },
        complete: () => {
          this.loading.set(false);
        },
      });
  }
}
